import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePerformanceBriefPrompts, type CreatePromptData } from '@/hooks/usePerformanceBriefPrompts';
import { usePerformanceBriefPromptDialog } from '@/contexts/PerformanceBriefPromptContext';

// Prompt types configuration
const PROMPT_TYPES = [
  { value: 'generate_brief', label: 'Generate Brief' },
  { value: 'landing_page_ai_summary', label: 'Landing Page AI Summary' },
] as const;

// Shortcodes for generate_brief type
const BRIEF_SHORTCODES = [
  { code: '{{brand_name}}', description: 'The name of the brand' },
  { code: '{{brand_description}}', description: 'The brand description/overview' },
  { code: '{{personas}}', description: 'Target personas with name, age, income, interests, pain points' },
  { code: '{{countries}}', description: 'List of target countries/markets' },
  { code: '{{engagement_points}}', description: 'Engagement points with name, type, description, funnel stage' },
];

// Shortcodes for landing_page_ai_summary type
const LANDING_PAGE_SHORTCODES = [
  { code: '{{page_url}}', description: 'The full URL of the landing page' },
  { code: '{{page_route}}', description: 'The route/path of the page' },
  { code: '{{page_content}}', description: 'The scraped content of the page' },
];

// Get shortcodes based on prompt type
const getShortcodesForType = (promptType: string) => {
  switch (promptType) {
    case 'landing_page_ai_summary':
      return LANDING_PAGE_SHORTCODES;
    case 'generate_brief':
    default:
      return BRIEF_SHORTCODES;
  }
};

const DEFAULT_BRIEF_TEMPLATE = `You are a performance marketing strategist. Generate a comprehensive performance marketing brief based on the following inputs:

## Brand
Name: {{brand_name}}
Description: {{brand_description}}

## Target Personas
{{personas}}

## Target Countries
{{countries}}

## Engagement Points
{{engagement_points}}

---

Please generate a detailed performance marketing brief that includes:
1. **Executive Summary** - Brief overview of the campaign strategy
2. **Target Audience Analysis** - Insights from the provided personas
3. **Geographic Strategy** - Market-specific recommendations
4. **Customer Journey Mapping** - How engagement points connect the funnel
5. **Key Messaging Framework** - Core messages for each persona/market
6. **Channel Recommendations** - Best channels based on personas and markets
7. **KPI Framework** - Measurable goals and benchmarks
8. **Implementation Roadmap** - Prioritized action items

Format the response in clean markdown with headers and bullet points.`;

const DEFAULT_LANDING_PAGE_TEMPLATE = `You are a landing page analyzer. Analyze the provided webpage content and extract:
1. A brief summary of what this page is about (2-3 sentences)
2. The primary purpose of this page (e.g., lead generation, product showcase, blog article, contact form, pricing page)
3. All call-to-action (CTA) buttons/links you can identify with their text, action URL (if visible), and type (button, link, form submit)
4. Key elements on the page (e.g., hero section, testimonials, features list, FAQ, pricing table)
5. The target audience this page is designed for

Page URL: {{page_url}}
Route: {{page_route}}

Page Content:
{{page_content}}

Return your analysis as a JSON object with these exact keys:
{
  "summary": "Brief description of the page",
  "purpose": "Primary purpose of the page",
  "ctas": [{"text": "CTA text", "action": "URL or action", "type": "button|link|form"}],
  "key_elements": ["element1", "element2"],
  "target_audience": "Description of target audience"
}

Only return valid JSON, no markdown code blocks or explanations.`;

// Get default template based on prompt type
const getDefaultTemplateForType = (promptType: string) => {
  switch (promptType) {
    case 'landing_page_ai_summary':
      return DEFAULT_LANDING_PAGE_TEMPLATE;
    case 'generate_brief':
    default:
      return DEFAULT_BRIEF_TEMPLATE;
  }
};

export const GlobalPerformanceBriefPromptDialog = () => {
  const { isOpen, editingPrompt, closeDialog } = usePerformanceBriefPromptDialog();
  const { isSaving, createPrompt, updatePrompt } = usePerformanceBriefPrompts();
  
  const [formData, setFormData] = useState<CreatePromptData>({
    name: '',
    prompt: DEFAULT_BRIEF_TEMPLATE,
    prompt_type: 'generate_brief',
  });

  // Reset form when dialog opens/closes or editing prompt changes
  useEffect(() => {
    if (isOpen) {
      if (editingPrompt) {
        setFormData({
          name: editingPrompt.name,
          prompt: editingPrompt.prompt,
          prompt_type: editingPrompt.prompt_type,
        });
      } else {
        setFormData({
          name: '',
          prompt: DEFAULT_BRIEF_TEMPLATE,
          prompt_type: 'generate_brief',
        });
      }
    }
  }, [isOpen, editingPrompt]);

  const handlePromptTypeChange = (newType: string) => {
    setFormData(prev => ({
      ...prev,
      prompt_type: newType,
      // Only update prompt if it's the default template for the old type
      prompt: prev.prompt === getDefaultTemplateForType(prev.prompt_type) 
        ? getDefaultTemplateForType(newType) 
        : prev.prompt,
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.prompt.trim()) return;

    if (editingPrompt) {
      await updatePrompt(editingPrompt.id, formData);
    } else {
      await createPrompt(formData);
    }
    closeDialog();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug
            popupName={editingPrompt ? 'Edit Prompt' : 'Create Prompt'}
            popupContext="/performance-brief/prompts"
            isOpen={isOpen}
          >
            {editingPrompt ? 'Edit Prompt' : 'Create New Prompt'}
          </DialogTitleWithBug>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="prompt-name">Prompt Name</Label>
            <Input
              id="prompt-name"
              placeholder="e.g., Standard Brief Generator"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prompt-type">Prompt Type</Label>
            <Select
              value={formData.prompt_type || 'generate_brief'}
              onValueChange={handlePromptTypeChange}
            >
              <SelectTrigger id="prompt-type">
                <SelectValue placeholder="Select prompt type" />
              </SelectTrigger>
              <SelectContent>
                {PROMPT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="prompt-content">Prompt Content</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {getShortcodesForType(formData.prompt_type).map((shortcode) => (
                <Badge 
                  key={shortcode.code} 
                  variant="outline" 
                  className="cursor-pointer hover:bg-muted"
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    prompt: prev.prompt + ' ' + shortcode.code 
                  }))}
                >
                  {shortcode.code}
                </Badge>
              ))}
            </div>
            <Textarea
              id="prompt-content"
              placeholder="Enter your prompt template..."
              className="min-h-[400px] font-mono text-sm"
              value={formData.prompt}
              onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={closeDialog}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !formData.name.trim() || !formData.prompt.trim()}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editingPrompt ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
